/**
 * Bomb detonation FX — the cool explosion that fires when a `bombs`-family
 * hazard crosses from its fly-in (telegraph) into its lethal blast (active)
 * phase. Purely decorative: collision still lives in `core` and the blast
 * geometry is owned by the {@link import('../core').CircleMoverHazard}; this only
 * paints the moment of detonation so it reads as a real bomb going off.
 *
 * It layers a fiery blast — white-hot → yellow → orange → deep-red ember — all
 * short-lived:
 *  1. a hard white **flash** disc that pops then collapses,
 *  2. an expanding **shockwave ring** (a single scaled+faded image),
 *  3. a radial **fire burst** of glowing embers that fan out, RISE (negative
 *     gravity) and fade like real flame, and
 *  4. a few slow, dark **smoke** puffs that drift up after the fire.
 *
 * PERFORMANCE: the burst reuses ONE pooled {@link Phaser.GameObjects.Particles.ParticleEmitter}
 * in explode mode (allocated lazily, kept across detonations, destroyed on
 * teardown). The flash and ring are two reused {@link Phaser.GameObjects.Image}
 * objects driven by tweens — no per-detonation allocation, no per-frame work in
 * the director. Each blast is sized to the hazard's blast radius so the picture
 * matches the lethal circle. The same render path runs during replays, so
 * recorded runs explode identically.
 */
import Phaser from 'phaser';
import { HAZARD_TEX, HAZARD_COLORS } from './textures';

/** Render depth — above hazards (5), below the player (10), so the ball stays readable. */
const FX_DEPTH = 8;

/** Total visual lifetime of one blast (ms); the lethal `activeMs` is independent. */
const FLASH_MS = 150;
const RING_MS = 460;
const SPARK_LIFE_MS = 620;

/** How many fire embers per blast — enough to read as a big fiery burst. */
const SPARK_COUNT = 64;
/** How many slow smoke puffs drift up after the fire. */
const SMOKE_COUNT = 10;

/** Author-time pixel size of the shockwave ring texture (matches textures.ts). */
const SHOCK_TEX = 128;
/** Author-time pixel size of the flash disc (the spark glow texture). */
const FLASH_TEX = 32;

/**
 * A reusable detonation effect. One instance per scene; call {@link blast} each
 * time a bomb detonates, and {@link destroy} on scene shutdown.
 */
export class Explosion {
  private readonly scene: Phaser.Scene;

  /** Lazily-created reusable fire-ember emitter (explode mode), shared by all blasts. */
  private emitter?: Phaser.GameObjects.Particles.ParticleEmitter;
  /** Lazily-created reusable smoke-puff emitter (explode mode). */
  private smoke?: Phaser.GameObjects.Particles.ParticleEmitter;
  /** Reusable flash disc + shockwave ring (hidden between blasts). */
  private flash?: Phaser.GameObjects.Image;
  private ring?: Phaser.GameObjects.Image;
  /** The live tweens, so we can stop/clean them on reset/teardown. */
  private flashTween?: Phaser.Tweens.Tween;
  private ringTween?: Phaser.Tweens.Tween;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  /**
   * Detonate at `(x, y)` with blast radius `r` (px) — the bomb's lethal radius,
   * so the FX footprint matches the collision circle. Safe to call rapidly; the
   * pooled objects are simply re-driven.
   */
  blast(x: number, y: number, r: number): void {
    this.ensureObjects();
    this.popFlash(x, y, r);
    this.popRing(x, y, r);
    this.popSparks(x, y, r);
    this.popSmoke(x, y, r);
    // A short kick so the detonation lands physically.
    this.scene.cameras.main.shake(200, 0.009);
  }

  /** Stop and hide everything (on `scene.restart()`); reusable afterwards. */
  reset(): void {
    this.flashTween?.stop();
    this.ringTween?.stop();
    this.flashTween = undefined;
    this.ringTween = undefined;
    this.flash?.setVisible(false).setActive(false);
    this.ring?.setVisible(false).setActive(false);
    this.emitter?.killAll();
    this.smoke?.killAll();
  }

  /** Destroy all render objects (scene shutdown). */
  destroy(): void {
    this.flashTween?.stop();
    this.ringTween?.stop();
    this.emitter?.destroy();
    this.smoke?.destroy();
    this.flash?.destroy();
    this.ring?.destroy();
    this.emitter = undefined;
    this.smoke = undefined;
    this.flash = undefined;
    this.ring = undefined;
  }

  /* --------------------------------------------------------------------- */

  /** Build the pooled emitters + flash/ring images once, on first use. */
  private ensureObjects(): void {
    if (!this.emitter) {
      this.emitter = this.scene.add
        .particles(0, 0, HAZARD_TEX.spark, {
          // Explode mode: no auto-flow; we burst on demand.
          frequency: -1,
          lifespan: { min: SPARK_LIFE_MS * 0.55, max: SPARK_LIFE_MS },
          // Radial burst; per-spark speed is overridden per blast (scaled to r).
          speed: { min: 80, max: 320 },
          angle: { min: 0, max: 360 },
          // Embers throw out then settle; the negative gravity makes the fire RISE
          // like real flame as each ember decelerates and fades.
          gravityY: -160,
          scale: { start: 1.05, end: 0, ease: 'quad.out' },
          alpha: { start: 1, end: 0, ease: 'quad.in' },
          rotate: { min: 0, max: 360 },
          // White-hot core → yellow → orange → deep-red ember over each ember's life.
          color: [
            HAZARD_COLORS.fireWhite,
            HAZARD_COLORS.fireYellow,
            HAZARD_COLORS.fireOrange,
            HAZARD_COLORS.fireRed,
            HAZARD_COLORS.fireEmber,
          ],
          colorEase: 'quad.out',
          blendMode: Phaser.BlendModes.ADD,
          emitting: false,
        })
        .setDepth(FX_DEPTH);
      // Pre-allocate the pool so the first detonation doesn't allocate.
      this.emitter.reserve(SPARK_COUNT * 2);
    }

    if (!this.smoke) {
      // Slow, dark smoke that drifts up and dissipates after the fire (NORMAL blend
      // so it darkens rather than adds light). Sits just under the embers.
      this.smoke = this.scene.add
        .particles(0, 0, HAZARD_TEX.spark, {
          frequency: -1,
          lifespan: { min: 700, max: 1200 },
          speed: { min: 20, max: 90 },
          angle: { min: 200, max: 340 }, // biased upward
          gravityY: -60,
          scale: { start: 0.7, end: 1.9, ease: 'quad.out' }, // billows out as it rises
          alpha: { start: 0.42, end: 0, ease: 'quad.in' },
          rotate: { min: 0, max: 360 },
          tint: [0x3a2a22, 0x21181a],
          blendMode: Phaser.BlendModes.NORMAL,
          emitting: false,
        })
        .setDepth(FX_DEPTH - 1);
      this.smoke.reserve(SMOKE_COUNT * 2);
    }

    if (!this.flash) {
      this.flash = this.scene.add
        .image(0, 0, HAZARD_TEX.spark)
        .setDepth(FX_DEPTH + 1)
        .setBlendMode(Phaser.BlendModes.ADD)
        .setVisible(false)
        .setActive(false);
    }

    if (!this.ring) {
      this.ring = this.scene.add
        .image(0, 0, HAZARD_TEX.shock)
        .setDepth(FX_DEPTH)
        .setBlendMode(Phaser.BlendModes.ADD)
        .setVisible(false)
        .setActive(false);
    }
  }

  /** A quick white-hot flash that pops big then collapses. */
  private popFlash(x: number, y: number, r: number): void {
    const flash = this.flash!;
    this.flashTween?.stop();
    // The flash disc covers roughly the inner blast; scale the 32px glow to ~1.4r.
    const peak = (r * 1.4 * 2) / FLASH_TEX;
    flash
      .setPosition(x, y)
      .setActive(true)
      .setVisible(true)
      .setAlpha(1)
      .setTint(HAZARD_COLORS.bombSparkCore)
      .setScale(peak * 0.4);
    this.flashTween = this.scene.tweens.add({
      targets: flash,
      scale: peak,
      alpha: 0,
      duration: FLASH_MS,
      ease: 'quad.out',
      onComplete: () => flash.setVisible(false).setActive(false),
    });
  }

  /** An expanding shockwave ring that thins and fades as it grows past `r`. */
  private popRing(x: number, y: number, r: number): void {
    const ring = this.ring!;
    this.ringTween?.stop();
    const startScale = (r * 0.5 * 2) / SHOCK_TEX;
    const endScale = (r * 1.85 * 2) / SHOCK_TEX;
    ring
      .setPosition(x, y)
      .setActive(true)
      .setVisible(true)
      .setAlpha(0.9)
      .setTint(HAZARD_COLORS.fireOrange)
      .setScale(startScale);
    this.ringTween = this.scene.tweens.add({
      targets: ring,
      scale: endScale,
      alpha: 0,
      duration: RING_MS,
      ease: 'cubic.out',
      onComplete: () => ring.setVisible(false).setActive(false),
    });
  }

  /** The radial spark burst — speed scaled so sparks reach roughly the blast edge. */
  private popSparks(x: number, y: number, r: number): void {
    const emitter = this.emitter!;
    // Sparks should fan out to about the blast radius over their lifetime. The
    // scale/alpha eases settle them, so aim the peak so the fastest sparks reach
    // ~r·1.15 in ~SPARK_LIFE_MS. `updateConfig({ speed })` keeps the emitter
    // radial (the `speed` key is a special-case override) while letting us rescale
    // the burst per blast — once per detonation, never per frame.
    const reach = r * 1.15;
    const peak = (reach / (SPARK_LIFE_MS / 1000)) * 1.3;
    emitter.updateConfig({ speed: { min: peak * 0.4, max: peak } });
    emitter.explode(SPARK_COUNT, x, y);
  }

  /** A few slow, dark smoke puffs that billow up after the fire. */
  private popSmoke(x: number, y: number, r: number): void {
    const smoke = this.smoke!;
    // Gentle outward speed scaled to the blast; the upward angle + gravity carry it up.
    const spread = Math.max(40, r * 0.7);
    smoke.updateConfig({ speed: { min: spread * 0.3, max: spread } });
    smoke.explode(SMOKE_COUNT, x, y);
  }
}
